import { useEffect, useMemo, useState } from "react";
import { BookMarked, Plus, Search, FolderPlus, TagIcon, Download, Trash2, Pencil, Copy, ExternalLink, FileText, Library as LibraryIcon, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import AddCitationDialog from "@/components/AddCitationDialog";
import CitationTable from "@/components/CitationTable";
import CitationInspector from "@/components/CitationInspector";
import EmptyState from "@/components/EmptyState";
import AppNav from "@/components/AppNav";
import CitationStyleSelector from "@/components/CitationStyleSelector";
import KeywordManagerDialog from "@/components/KeywordManagerDialog";
import UserMenu from "@/components/UserMenu";
import { useCitationStyle } from "@/hooks/useCitationStyle";
import { useKeywordColors } from "@/hooks/useKeywordColors";
import {
  listFolders,
  listTags,
  listCitations,
  createFolder,
  deleteFolder,
  createTag,
  deleteTag,
  deleteCitation,
  updateCitation,
  exportAllBibtex,
  exportAllStyle,
} from "@/lib/api";

export default function Library() {
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [style, setStyle] = useCitationStyle();
  const { colorOf: keywordColor, refresh: refreshKeywordColors } = useKeywordColors();

  const [selectedFolder, setSelectedFolder] = useState("all"); // 'all', 'unfiled', or folder id
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [inspectId, setInspectId] = useState(null);
  const [keywordManagerOpen, setKeywordManagerOpen] = useState(false);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 1280px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const fn = (e) => setWide(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const handleToggleRead = async (c) => {
    setCitations((prev) => prev.map((x) => (x.id === c.id ? { ...x, read: !c.read } : x)));
    try {
      await updateCitation(c.id, { read: !c.read });
    } catch {
      setCitations((prev) => prev.map((x) => (x.id === c.id ? { ...x, read: c.read } : x)));
      toast.error("Couldn't update read status");
    }
  };

  const refreshAll = async () => {
    const [f, t, c] = await Promise.all([listFolders(), listTags(), listCitations()]);
    setFolders(f);
    setTags(t);
    setCitations(c);
  };

  useEffect(() => {
    refreshAll().finally(() => setLoading(false));
  }, []);

  const handleCreateFolder = async (name) => {
    const f = await createFolder(name);
    setFolders((prev) => [...prev, f]);
    toast.success(`Project "${f.name}" created`);
  };

  const handleDeleteFolder = async (id) => {
    await deleteFolder(id);
    setFolders((prev) => prev.filter((x) => x.id !== id));
    setCitations((prev) => prev.map((c) => (c.folder_id === id ? { ...c, folder_id: null } : c)));
    if (selectedFolder === id) setSelectedFolder("all");
    toast.success("Project removed");
  };

  const handleCreateTag = async (name, color) => {
    const t = await createTag(name, color);
    setTags((prev) => [...prev, t]);
    toast.success(`Tag "${t.name}" added`);
  };

  const handleDeleteTag = async (id) => {
    await deleteTag(id);
    setTags((prev) => prev.filter((x) => x.id !== id));
    setCitations((prev) => prev.map((c) => ({ ...c, tag_ids: c.tag_ids.filter((tid) => tid !== id) })));
    setSelectedTagIds((prev) => prev.filter((x) => x !== id));
    toast.success("Tag removed");
  };

  const handleDeleteCitation = async (id) => {
    await deleteCitation(id);
    setCitations((prev) => prev.filter((c) => c.id !== id));
    if (inspectId === id) setInspectId(null);
    toast.success("Citation deleted");
  };

  const filtered = useMemo(() => {
    let list = citations;
    if (selectedFolder === "unfiled") list = list.filter((c) => !c.folder_id);
    else if (selectedFolder !== "all") list = list.filter((c) => c.folder_id === selectedFolder);
    if (selectedTagIds.length > 0)
      list = list.filter((c) => selectedTagIds.every((tid) => c.tag_ids?.includes(tid)));
    if (selectedKeywords.length > 0) {
      const lower = selectedKeywords.map((k) => k.toLowerCase());
      list = list.filter((c) => {
        const kws = (c.keywords || []).map((k) => k.toLowerCase());
        return lower.every((k) => kws.includes(k));
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        [c.title, c.journal, c.doi, c.abstract, c.notes, c.methodology, c.population, c.main_findings, ...(c.authors || []), ...(c.keywords || [])]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return list;
  }, [citations, selectedFolder, selectedTagIds, selectedKeywords, search]);

  const handleExportAll = async () => {
    const folderId = selectedFolder !== "all" && selectedFolder !== "unfiled" ? selectedFolder : null;
    const text = await exportAllBibtex(folderId);
    if (!text.trim()) return toast.error("Nothing to export");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "citations.bib";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("BibTeX exported");
  };

  useEffect(() => {
    if (inspectId && !filtered.some((c) => c.id === inspectId)) setInspectId(null);
  }, [filtered, inspectId]);

  const inspectCitation = citations.find((c) => c.id === inspectId);

  const activeFolderName =
    selectedFolder === "all"
      ? "All Citations"
      : selectedFolder === "unfiled"
      ? "Unfiled"
      : folders.find((f) => f.id === selectedFolder)?.name || "Library";

  return (
    <div className="h-screen overflow-hidden bg-[#FFF5FA] dark:bg-[#1E1529] paper-grain flex" data-testid="app-shell">
      <Sidebar
        folders={folders}
        tags={tags}
        citations={citations}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        selectedTagIds={selectedTagIds}
        setSelectedTagIds={setSelectedTagIds}
        selectedKeywords={selectedKeywords}
        setSelectedKeywords={setSelectedKeywords}
        onCreateFolder={handleCreateFolder}
        onDeleteFolder={handleDeleteFolder}
        onCreateTag={handleCreateTag}
        onDeleteTag={handleDeleteTag}
        onExportAll={handleExportAll}
        onManageKeywords={() => setKeywordManagerOpen(true)}
        keywordColor={keywordColor}
      />

      <main className="flex-1 min-w-0 flex flex-col h-screen">
        {/* Header */}
        <header
          data-testid="app-header"
          className="sticky top-0 z-20 bg-[#FFF5FA]/85 dark:bg-[#1E1529]/85 backdrop-blur border-b border-[#F5D0E3] dark:border-[#45325A]"
        >
          <div className="px-6 py-4 flex items-center gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#F472B6] to-[#A78BFA] text-white grid place-items-center shadow-[0_4px_14px_rgba(244,114,182,0.45)] -rotate-6">
                <BookMarked className="w-5 h-5" />
              </div>
              <div>
                <div className="font-serif-display text-xl font-semibold leading-none text-[#3B2350] dark:text-[#FBEFF6]">
                  LitMap
                </div>
              </div>
            </div>

            <AppNav />

            <div className="flex-1 relative max-w-md mx-auto">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A8C]" />
              <Input
                data-testid="search-citation-input"
                placeholder="Search titles, authors, DOIs, notes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 bg-white dark:bg-[#3B2350] border-[#F5D0E3] dark:border-[#45325A] rounded-lg"
              />
            </div>

            <CitationStyleSelector value={style} onChange={setStyle} />

            <Button
              data-testid="add-citation-open-modal-button"
              onClick={() => setAddOpen(true)}
              className="bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white h-10 rounded-lg gap-2 shrink-0"
            >
              <Plus className="w-4 h-4" /> Add citation
            </Button>

            <UserMenu />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="px-6 py-6">
            <div className="flex items-end justify-between mb-5 fade-slide-in">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#7A6A8C] dark:text-[#8E7BA6] font-medium">
                  {selectedFolder === "all" ? "Full library" : selectedFolder === "unfiled" ? "Unfiled items" : "Project"}
                </div>
                <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#3B2350] dark:text-[#FBEFF6] mt-1">
                  {activeFolderName}
                </h1>
                <p className="text-sm text-[#5E4B70] dark:text-[#BBA9CF] mt-2">
                  {filtered.length} {filtered.length === 1 ? "citation" : "citations"}
                  {selectedTagIds.length > 0 && ` • ${selectedTagIds.length} tag${selectedTagIds.length > 1 ? "s" : ""}`}
                  {selectedKeywords.length > 0 && ` • ${selectedKeywords.length} keyword${selectedKeywords.length > 1 ? "s" : ""}`}
                </p>
              </div>

              {(selectedTagIds.length > 0 || selectedKeywords.length > 0) && (
                <button
                  onClick={() => {
                    setSelectedTagIds([]);
                    setSelectedKeywords([]);
                  }}
                  className="text-xs text-[#C9286F] dark:text-[#C4A1FF] hover:underline flex items-center gap-1"
                  data-testid="clear-tag-filters"
                >
                  <X className="w-3 h-3" /> Clear filters
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-sm text-[#7A6A8C]">Loading your library…</div>
            ) : filtered.length === 0 ? (
              <EmptyState
                hasCitations={citations.length > 0}
                onAdd={() => setAddOpen(true)}
                searching={!!search}
              />
            ) : (
              <CitationTable
                citations={filtered}
                selectedId={inspectId}
                onSelect={setInspectId}
                onToggleRead={handleToggleRead}
                onDelete={handleDeleteCitation}
              />
            )}
          </div>
        </div>
      </main>

      <AddCitationDialog
        open={addOpen}
        setOpen={setAddOpen}
        folders={folders}
        tags={tags}
        onCreated={(c) => setCitations((prev) => [c, ...prev])}
      />

      <CitationInspector
        citation={inspectCitation}
        folders={folders}
        tags={tags}
        allCitations={citations}
        style={style}
        keywordColor={keywordColor}
        onClose={() => setInspectId(null)}
        onUpdate={(updated) => setCitations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
        onDelete={(id) => handleDeleteCitation(id)}
        inline={wide}
      />
      {wide && !inspectCitation && (
        <aside
          data-testid="inspector-placeholder"
          className="w-[460px] shrink-0 h-screen border-l border-[#F5D0E3] dark:border-[#45325A] bg-white/60 dark:bg-[#2E2140] grid place-items-center p-10 text-center"
        >
          <div>
            <div className="font-serif-display text-xl text-[#3B2350] dark:text-[#FBEFF6]">No paper selected</div>
            <p className="text-sm text-[#5E4B70] mt-2">Click a paper in the list to see its details and notes here.</p>
          </div>
        </aside>
      )}

      <KeywordManagerDialog
        open={keywordManagerOpen}
        setOpen={setKeywordManagerOpen}
        onChanged={() => {
          refreshAll();
          refreshKeywordColors();
        }}
      />
    </div>
  );
}
